import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { enumAttribute, primitiveStyles } from '../shared/primitive-utils.js';

/**
 * A horizontal or vertical content boundary, optionally labelled or decorative.
 * @element qds-separator
 * @attr {'horizontal'|'vertical'} orientation - Boundary direction. Default horizontal.
 * @attr {string} label - Optional visible text and semantic accessible name.
 * @attr {boolean} decorative - Hide the boundary from assistive technology.
 * @property {'horizontal'|'vertical'} orientation - Boundary direction.
 * @property {string} label - Visible label; text is never parsed as markup.
 * @property {boolean} decorative - Decorative boundaries have no separator role.
 * @csspart line - Boundary lines.
 * @csspart label - Optional visible label.
 */
export class QdsSeparator extends HTMLElement {
  static get observedAttributes(): string[] { return ['orientation', 'label', 'decorative']; }
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: flex; align-items: center; gap: 12px; margin-block: 16px; }
      [part=line] { flex: 1; border-top: 1px solid var(--theme-border-primary, #e0e0e0); }
      [part=label] { color: var(--theme-text-primary, #333); font-size: 0.875em; }
      :host([orientation=vertical]) { flex-direction: column; align-self: stretch; margin: 0 16px; min-height: 2.75rem; }
      :host([orientation=vertical]) [part=line] { border-top: 0; border-inline-start: 1px solid var(--theme-border-primary, #e0e0e0); }
      @media (forced-colors: active) { [part=line] { border-color: CanvasText; } }
    </style><span part="line"></span><span part="label"></span><span part="line" class="trailing"></span>`;
    loadBaseCSS();
  }
  connectedCallback(): void { for (const name of ['orientation', 'label', 'decorative']) upgradeProperty(this, name); this._sync(); }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get orientation(): 'horizontal' | 'vertical' { return enumAttribute(this, 'orientation', ['horizontal', 'vertical'], 'horizontal'); }
  set orientation(value: 'horizontal' | 'vertical') { this.setAttribute('orientation', value); }
  get label(): string { return this.getAttribute('label') || ''; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  get decorative(): boolean { return this.hasAttribute('decorative'); }
  set decorative(value: boolean) { this.toggleAttribute('decorative', !!value); }

  private _sync(): void {
    // Normalize invalid directions so visual layout and semantic direction agree.
    if (this.hasAttribute('orientation') && this.getAttribute('orientation') !== this.orientation) this.setAttribute('orientation', this.orientation);
    if (this.decorative) {
      this.removeAttribute('role'); this.removeAttribute('aria-orientation'); this.removeAttribute('aria-label'); this.setAttribute('aria-hidden', 'true');
    } else {
      this.setAttribute('role', 'separator'); this.setAttribute('aria-orientation', this.orientation); this.removeAttribute('aria-hidden');
      if (this.label) this.setAttribute('aria-label', this.label); else this.removeAttribute('aria-label');
    }
    const label = this.shadowRoot!.querySelector<HTMLElement>('[part=label]')!;
    label.textContent = this.label; label.hidden = !this.label;
    this.shadowRoot!.querySelector<HTMLElement>('.trailing')!.hidden = !this.label;
  }
}
if (!customElements.get('qds-separator')) customElements.define('qds-separator', QdsSeparator);
